import { z } from "zod";
import { validateUsername } from "../../lib/username-validation";
import { ALL_GRADES, isValidGrade, type Grade } from "../../lib/grades";
import type { AccountRole } from "./types";

export type BulkAccountRole = Extract<AccountRole, "STUDENT" | "TEACHER">;
const BULK_ACCOUNT_ROLES: BulkAccountRole[] = ["STUDENT", "TEACHER"];

export const BULK_USER_CSV_HEADERS = [
  "name",
  "email",
  "password",
  "username",
  "account_role",
  "organization",
  "grade",
  "classroom",
] as const;

export type BulkUserCsvHeader = (typeof BULK_USER_CSV_HEADERS)[number];

export type BulkUserDraft = Record<BulkUserCsvHeader, string>;

export type BulkUserInput = {
  name: string;
  email: string;
  password: string;
  username?: string;
  account_role: BulkAccountRole;
  // Only meaningful when account_role is "STUDENT"; one of ALL_GRADES.
  grade?: string;
  organization?: string;
  classroom?: string;
};

export type BulkUserValidationError = {
  row: number;
  field?: BulkUserCsvHeader;
  message: string;
};

export const createEmptyBulkUserDraft = (): BulkUserDraft => ({
  name: "",
  email: "",
  password: "",
  username: "",
  account_role: "",
  organization: "",
  grade: "",
  classroom: "",
});

const bulkUserDraftSchema = z
  .object({
    name: z.string(),
    email: z.string(),
    password: z.string(),
    username: z.string(),
    account_role: z.string(),
    organization: z.string(),
    grade: z.string(),
    classroom: z.string(),
  })
  .strict();

const EMAIL_SCHEMA = z.string().email();

const accountRoleHelpText = `Allowed account_role: ${BULK_ACCOUNT_ROLES.join(", ")}.`;
const gradeHelpText = `Allowed grade: ${ALL_GRADES.join(", ")}.`;

const canonicalizeAccountRole = (raw: string): BulkAccountRole | null => {
  const normalized = raw.trim().toLowerCase();
  if (normalized === "student") return "STUDENT";
  if (normalized === "teacher") return "TEACHER";
  return null;
};

const canonicalizeGrade = (raw: string): Grade | null => {
  const normalized = raw.trim().toUpperCase();
  return isValidGrade(normalized) ? normalized : null;
};

const bulkUserInputSchema = bulkUserDraftSchema.transform((draft, ctx) => {
  let hasIssues = false;
  const addIssue = (field: BulkUserCsvHeader, message: string) => {
    hasIssues = true;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [field],
      message,
    });
  };

  const email = draft.email.trim().toLowerCase();
  if (!email) {
    addIssue("email", "Email is required.");
  } else if (!EMAIL_SCHEMA.safeParse(email).success) {
    addIssue("email", "Email must be a valid email address.");
  }

  const password = draft.password;
  if (!password.trim()) {
    addIssue("password", "Password is required.");
  } else if (password.length < 8 || password.length > 128) {
    addIssue("password", "Password must be 8–128 characters.");
  }

  const username = draft.username.trim();
  if (username) {
    const usernameError = validateUsername(username);
    if (usernameError) {
      addIssue("username", usernameError);
    }
  }

  const accountRole = canonicalizeAccountRole(draft.account_role);
  if (!accountRole) {
    addIssue(
      "account_role",
      `Unsupported account_role: "${draft.account_role.trim()}". ${accountRoleHelpText}`,
    );
  }

  const rawGrade = draft.grade.trim();
  const grade = rawGrade ? canonicalizeGrade(rawGrade) : null;
  if (rawGrade && accountRole === "STUDENT") {
    if (!grade) {
      addIssue("grade", `Unsupported grade: "${rawGrade}". ${gradeHelpText}`);
    }
  }

  const organization = draft.organization.trim();
  const classroom = draft.classroom.trim();
  if (accountRole === "STUDENT" && !organization) {
    addIssue("organization", "Organization is required for students.");
  }

  if (hasIssues || !accountRole) return z.NEVER;

  const emailPrefix = email.includes("@") ? (email.split("@")[0] ?? "") : "";
  const name = draft.name.trim() || emailPrefix || "User";

  const baseInput: BulkUserInput = {
    name,
    email,
    password,
    account_role: accountRole,
  };

  if (organization) baseInput.organization = organization;
  if (accountRole === "STUDENT" && classroom) baseInput.classroom = classroom;

  if (username) {
    baseInput.username = username;
  }

  if (accountRole === "STUDENT" && grade) {
    baseInput.grade = grade;
  }

  return baseInput;
});

const normalizeCsvContent = (content: string): string =>
  content.replace(/^\uFEFF/, "");

const parseCsvRows = (content: string): string[][] => {
  const normalized = normalizeCsvContent(content);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  for (let index = 0; index < normalized.length; index += 1) {
    const char = normalized[index];
    if (char === undefined) continue;

    if (char === '"') {
      const nextChar = normalized[index + 1];
      if (inQuotes && nextChar === '"') {
        field += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(field);
      field = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";

      if (char === "\r" && normalized[index + 1] === "\n") {
        index += 1;
      }
      continue;
    }

    field += char;
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  if (inQuotes) throw new Error("CSV contains an unclosed quoted field.");
  return rows;
};

export const parseBulkUserCsv = (
  csvContent: string,
): { drafts: BulkUserDraft[]; errors: string[]; rowNumbers?: number[] } => {
  let rows: string[][];
  try { rows = parseCsvRows(csvContent); }
  catch (error) { return { drafts: [], errors: [error instanceof Error ? error.message : "Invalid CSV."] }; }
  if (rows.length === 0) {
    return { drafts: [], errors: ["CSV file is empty."] };
  }

  const headerRow = rows[0];
  if (!headerRow) {
    return { drafts: [], errors: ["Missing CSV header row."] };
  }

  const normalizedHeaders = headerRow.map((header) =>
    header.trim().toLowerCase(),
  );
  const requiredHeaders = ["name", "email", "password", "username", "account_role"];
  const missing = requiredHeaders.filter((header) => !normalizedHeaders.includes(header));
  const unknown = normalizedHeaders.filter((header) => !(BULK_USER_CSV_HEADERS as readonly string[]).includes(header));
  const duplicates = normalizedHeaders.length !== new Set(normalizedHeaders).size;
  if (missing.length || unknown.length || duplicates) {
    return { drafts: [], errors: [`Invalid CSV headers. Expected: ${BULK_USER_CSV_HEADERS.join(", ")}. Download a fresh template.`] };
  }

  const columnCount = normalizedHeaders.length;

  const drafts: BulkUserDraft[] = [];
  const rowNumbers: number[] = [];
  const errors: string[] = [];

  for (let index = 1; index < rows.length; index += 1) {
    const row = rows[index];
    if (!row) continue;

    if (row.every((cell) => cell.trim().length === 0)) {
      continue;
    }

    const rowNumber = index + 1;
    if (row.length !== columnCount) {
      errors.push(
        `Row ${rowNumber} must have exactly ${columnCount} columns.`,
      );
      continue;
    }

    const draft = createEmptyBulkUserDraft();
    normalizedHeaders.forEach((header, column) => {
      const key = header as BulkUserCsvHeader;
      draft[key] = key === "password" ? row[column] ?? "" : row[column]?.trim() ?? "";
    });
    drafts.push(draft);
    rowNumbers.push(rowNumber);
  }

  if (drafts.length === 0 && errors.length === 0) {
    errors.push("No user rows found in CSV.");
  }

  return { drafts, errors, rowNumbers };
};

const issueToValidationError = (
  issue: z.ZodIssue,
  row: number,
): BulkUserValidationError => {
  const pathHead = issue.path[0];
  const field = BULK_USER_CSV_HEADERS.find((header) => header === pathHead);
  return {
    row,
    field,
    message: issue.message,
  };
};

export const validateBulkUserDrafts = (
  drafts: BulkUserDraft[],
  rowOffset = 1,
  rowNumbers?: number[],
): { users: BulkUserInput[]; errors: BulkUserValidationError[] } => {
  const users: BulkUserInput[] = [];
  const errors: BulkUserValidationError[] = [];

  drafts.forEach((draft, index) => {
    const parsed = bulkUserInputSchema.safeParse(draft);
    const rowNumber = rowNumbers?.[index] ?? rowOffset + index;

    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        errors.push(issueToValidationError(issue, rowNumber));
      });
      return;
    }

    users.push(parsed.data);
  });

  const seenEmails = new Map<string, number>();
  const seenUsernames = new Map<string, number>();

  drafts.forEach((draft, index) => {
    const user = {
      email: typeof draft.email === "string" ? draft.email.trim().toLowerCase() : "",
      username: typeof draft.username === "string" ? draft.username.trim() : "",
    };
    const rowNumber = rowNumbers?.[index] ?? rowOffset + index;
    const existingEmailRow = seenEmails.get(user.email);

    if (user.email && existingEmailRow !== undefined) {
      errors.push({
        row: rowNumber,
        field: "email",
        message: `Duplicate email. Also used in row ${existingEmailRow}.`,
      });
    } else if (user.email) {
      seenEmails.set(user.email, rowNumber);
    }

    if (user.username) {
      const usernameKey = user.username.toLowerCase();
      const existingUsernameRow = seenUsernames.get(usernameKey);

      if (existingUsernameRow !== undefined) {
        errors.push({
          row: rowNumber,
          field: "username",
          message: `Duplicate username. Also used in row ${existingUsernameRow}.`,
        });
      } else {
        seenUsernames.set(usernameKey, rowNumber);
      }
    }
  });

  if (errors.length > 0) {
    return { users: [], errors };
  }

  return { users, errors: [] };
};

export const formatBulkUserValidationErrors = (
  errors: BulkUserValidationError[],
): string[] =>
  errors.map((error) =>
    error.field
      ? `Row ${error.row} (${error.field}): ${error.message}`
      : `Row ${error.row}: ${error.message}`,
  );

export const getBulkUserCsvTemplate = (): string =>
  [
    BULK_USER_CSV_HEADERS.join(","),
    "Jane Doe,jane.doe@example.com,ChangeMe123!,jane.doe,STUDENT,Example School,6,Math 6",
    "Sam Teacher,sam.teacher@example.com,ChangeMe123!,sam.teacher,TEACHER,Example School,,",
  ].join("\n");
